/**
 * The email signup popup. 00-docs/specs/email-popup.md is the authority; the
 * markup is snippets/mab-email-popup.liquid, which documents the data contract
 * this file reads.
 *
 * Loaded as a deferred module and started on idle. It asks once, gets out of
 * the way, and never interrupts a purchase or a photo upload.
 *
 *   Triggers   settings.email_popup_delay_seconds (default 8) after the page's
 *              load event, or, on a desktop with a fine hovering pointer and a
 *              window at least 750px wide, a mouse leaving through the top
 *              edge. Whichever comes first disarms the other, and the session
 *              flag stops both for the rest of the visit.
 *   Product    pages: exit intent only, no timer (owner ruling R1, 4 Oct). An
 *              exit while something is in the way does nothing and queues
 *              nothing; a later exit, once clear, can still open it. Phones
 *              never see it there.
 *   Waits      (other pages) while the floating add to cart bar is stuck, any
 *              other modal dialog or scroll-locked panel (the menu) is open, a
 *              locket upload has a photo placed or its cropper open, or
 *              Shopify's privacy banner is showing (its element rendered, or
 *              shouldShowBanner() true). A pending open looks again every
 *              300 ms and on visitorConsentCollected, and fires as soon as
 *              nothing is in the way, in the same page view.
 *   Remembers  localStorage mab:email-popup {state, at}: closed or signed up
 *              suppresses it for 30 days. Bad JSON, or an at more than a day
 *              in the future, counts as absent. If localStorage cannot be read
 *              and written it never auto-opens, because it could not keep the
 *              30-day promise. Closing it while it shows a server error
 *              records nothing.
 *   Signup     a normal form post, never fetch. On submit a sessionStorage
 *              marker is written; it survives a /challenge round trip in the
 *              same tab, because the script does nothing at all on /challenge
 *              and clears the marker only on the load that shows the result.
 *              The popup reopens in its success or error state only when its
 *              own marker is present and under 15 minutes old.
 *
 * The decisions are the exported pure functions below, so they can be tested
 * without a browser (00-docs/checks/email-popup.mjs). Everything that touches
 * the DOM or storage is under boot(), which only runs in a page.
 */

export const STATE_KEY = 'mab:email-popup';
export const SHOWN_KEY = 'mab:email-popup:shown';
export const POSTED_KEY = 'mab:email-popup:posted';
/** 30 days, in ms. */
export const SUPPRESS_MS = 2592000000;
/** How long a submit marker counts as this tab's own signup, in ms. */
export const MARKER_MS = 15 * 60 * 1000;
/** The timer, in ms after the load event, when the setting is missing or out of range. */
export const DELAY_MS = 8000;
/** The delay setting's range, in seconds, as in settings_schema.json. */
export const DELAY_MIN_S = 3;
export const DELAY_MAX_S = 60;
/** A stored record dated further ahead than this is not trusted, in ms. */
export const FUTURE_SLACK_MS = 86400000;
/** How often a pending open looks again at what was in the way, in ms. */
export const RETRY_MS = 300;

/**
 * The stored record, or null when there is none or it is not one of ours.
 * @param {string | null | undefined} raw
 * @returns {{state: 'dismissed' | 'subscribed', at: number} | null}
 */
export function parseStoredState(raw) {
  if (typeof raw !== 'string' || raw === '') return null;
  let value;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!value || typeof value !== 'object') return null;
  if (value.state !== 'dismissed' && value.state !== 'subscribed') return null;
  if (typeof value.at !== 'number' || !Number.isFinite(value.at)) return null;
  return { state: value.state, at: value.at };
}

/**
 * True while a stored record still holds the popup back: now - at under 30
 * days. A record dated more than a day ahead of now (a wrong clock, or not
 * ours) counts as absent.
 * @param {{state: string, at: number} | null} record
 * @param {number} now
 */
export function isSuppressed(record, now) {
  if (!record) return false;
  if (record.at > now + FUTURE_SLACK_MS) return false;
  return now - record.at < SUPPRESS_MS;
}

/**
 * The timer in ms from the delay setting, in seconds. Missing, not a number or
 * outside the setting's range gives the default.
 * @param {string | number | null | undefined} raw
 */
export function delayFrom(raw) {
  if (raw === null || raw === undefined || raw === '') return DELAY_MS;
  const seconds = Number(raw);
  if (!Number.isFinite(seconds) || seconds < DELAY_MIN_S || seconds > DELAY_MAX_S) return DELAY_MS;
  return Math.round(seconds * 1000);
}

/**
 * Which triggers a page arms. On a product page the timer never runs and a
 * blocked exit is not queued (owner ruling R1); elsewhere both run and a
 * blocked open waits until the way is clear.
 * @param {{productPage: boolean, exitArmed: boolean}} input
 * @returns {{timer: boolean, exitIntent: boolean, queueWhenBlocked: boolean}}
 */
export function planTriggers(input) {
  return {
    timer: !input.productPage,
    exitIntent: !!input.exitArmed,
    queueWhenBlocked: !input.productPage,
  };
}

/**
 * True when this load is the one that handles a submit's result, so the
 * marker has done its job and is cleared. On any other load (a /challenge
 * round trip, a page between) it is left alone.
 * @param {{posted: boolean, hasError: boolean}} input
 */
export function markerHandled(input) {
  return !!(input.posted || input.hasError);
}

/** True for Shopify's bot challenge, with or without a locale prefix. */
export function isChallengePath(pathname) {
  return /^(\/[a-z]{2}(-[a-z]{2,4})?)?\/challenge(\/|$)/i.test(String(pathname || ''));
}

/**
 * A click that closes the dialog from its backdrop: the press and the click
 * both landed on the dialog element itself, outside its box. A drag that
 * starts in the panel and ends on the backdrop (selecting text) is not one.
 * @param {{target: unknown, x: number, y: number} | null} down
 * @param {{target: unknown, x: number, y: number}} up
 * @param {unknown} dialog
 * @param {{left: number, right: number, top: number, bottom: number}} box
 */
export function isBackdropClick(down, up, dialog, box) {
  const outside = (p) => p.x < box.left || p.x > box.right || p.y < box.top || p.y > box.bottom;
  if (!down || down.target !== dialog || up.target !== dialog) return false;
  return outside(down) && outside(up);
}

/**
 * True when a submit marker is this tab's own and recent: present and under
 * 15 minutes old. A marker from the future is not trusted.
 * @param {string | number | null | undefined} marker
 * @param {number} now
 */
export function markerFresh(marker, now) {
  if (marker === null || marker === undefined || marker === '') return false;
  const at = Number(marker);
  if (!Number.isFinite(at)) return false;
  const age = now - at;
  return age >= 0 && age < MARKER_MS;
}

/**
 * What to do when the page loads.
 *
 * @param {{
 *   checkoutPath: boolean,
 *   designMode: boolean,
 *   posted: boolean,
 *   hasError: boolean,
 *   marker: string | number | null,
 *   now: number,
 *   storageOk: boolean,
 *   stored: string | null,
 *   shown: boolean,
 *   knownSubscriber: boolean,
 * }} input
 * @returns {{action: 'none' | 'design' | 'success' | 'error' | 'arm', write: null | 'subscribed'}}
 */
export function decideOnLoad(input) {
  if (input.checkoutPath) return { action: 'none', write: null };
  if (input.designMode) return { action: 'design', write: null };
  const own = markerFresh(input.marker, input.now);
  if (input.posted && own) return { action: 'success', write: 'subscribed' };
  if (input.hasError && own) return { action: 'error', write: null };
  // Another form on the page signed someone up: never claim it, never offer
  // them a code twice.
  if (input.posted) return { action: 'none', write: 'subscribed' };
  if (input.knownSubscriber) return { action: 'none', write: null };
  if (!input.storageOk) return { action: 'none', write: null };
  if (isSuppressed(parseStoredState(input.stored), input.now)) return { action: 'none', write: null };
  if (input.shown) return { action: 'none', write: null };
  return { action: 'arm', write: null };
}

/** True for any checkout path; checkout is not theme-rendered, but never there. */
export function isCheckoutPath(pathname) {
  return /\/checkouts(\/|$)/.test(String(pathname || ''));
}

/**
 * Exit intent is armed only on a desktop: a fine pointer that can hover, and a
 * window at least 750px wide.
 * @param {(query: string) => {matches: boolean}} matchMedia
 * @param {number} width
 */
export function exitIntentArmed(matchMedia, width) {
  if (typeof matchMedia !== 'function') return false;
  let fine = false;
  try {
    fine = matchMedia('(hover: hover) and (pointer: fine)').matches;
  } catch {
    fine = false;
  }
  return fine && width >= 750;
}

/**
 * A mouse leaving the window through the top edge.
 * @param {{relatedTarget?: unknown, clientY?: number}} event
 */
export function isExitEvent(event) {
  if (!event) return false;
  return (event.relatedTarget === null || event.relatedTarget === undefined) && typeof event.clientY === 'number' && event.clientY <= 0;
}

/**
 * The success line with its code, as the success state reads, for tests.
 * @param {string} line
 * @param {string} code
 */
export function successText(line, code) {
  return line.includes('[code]') ? line.split('[code]').join(code) : `${line} ${code}`;
}

/** The element Shopify's privacy banner renders as. */
export const PRIVACY_BANNER_ID = 'shopify-pc__banner';

/**
 * True while Shopify's privacy banner is waiting on the visitor: its element
 * is rendered (it has a box, is not display none, is not visibility hidden),
 * or the API says it should show. The element is read first because the API
 * is not enough: in Manitoba (region CAMB) the banner shows while
 * shouldShowBanner() returns false. Shopify hides the element with display
 * none once it is answered. Each read is guarded on its own and any failure
 * reads as no banner: both are Shopify's, loaded on its own schedule.
 * @param {{
 *   document?: {getElementById?: (id: string) => {getClientRects?: () => {length: number}} | null},
 *   getComputedStyle?: (el: unknown) => {display?: string, visibility?: string},
 *   Shopify?: {customerPrivacy?: {shouldShowBanner?: () => unknown}},
 * } | null | undefined} win
 */
export function privacyBannerShowing(win) {
  try {
    const el = win?.document?.getElementById?.(PRIVACY_BANNER_ID);
    if (el && typeof el.getClientRects === 'function' && el.getClientRects().length > 0) {
      const style = typeof win?.getComputedStyle === 'function' ? win.getComputedStyle(el) : null;
      if (!style || (style.display !== 'none' && style.visibility !== 'hidden' && style.visibility !== 'collapse')) return true;
    }
  } catch {
    /* no readable element; the API may still answer */
  }
  try {
    const privacy = win?.Shopify?.customerPrivacy;
    if (!privacy || typeof privacy.shouldShowBanner !== 'function') return false;
    return privacy.shouldShowBanner() === true;
  } catch {
    return false;
  }
}

/** What is in the way of opening right now, or null. */
export function blockedBy(doc, dialog, win) {
  if (doc.querySelector("sticky-add-to-cart[data-variant-available='true'] .sticky-add-to-cart__bar[data-stuck='true']")) {
    return 'sticky-bar';
  }
  let modal = null;
  try {
    modal = [...doc.querySelectorAll('dialog:modal')].find((d) => d !== dialog) ?? null;
  } catch {
    modal = [...doc.querySelectorAll('dialog[open]')].find((d) => d !== dialog) ?? null;
  }
  if (modal) return 'modal';
  // The menu drawer and the theme's other scroll-locked panels: dialog.js
  // sets html[scroll-lock] while one is open.
  if (doc.querySelector('html[scroll-lock], details[scroll-lock][open]')) return 'scroll-lock';
  if (doc.querySelector("mab-locket-upload[data-state]:not([data-state='empty'])")) return 'locket-upload';
  if (privacyBannerShowing(win)) return 'privacy-banner';
  return null;
}

/* ---- storage, every call in try/catch -------------------------------------- */

function storageGet(area, key) {
  try {
    return window[area].getItem(key);
  } catch {
    return null;
  }
}

function storageSet(area, key, value) {
  try {
    window[area].setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function storageRemove(area, key) {
  try {
    window[area].removeItem(key);
  } catch {
    /* nothing to do: the marker simply cannot be cleared */
  }
}

/** True when localStorage can be both read and written. */
function localStorageWorks() {
  const probe = `${STATE_KEY}:probe`;
  try {
    window.localStorage.setItem(probe, '1');
    const ok = window.localStorage.getItem(probe) === '1';
    window.localStorage.removeItem(probe);
    return ok;
  } catch {
    return false;
  }
}

function writeState(state) {
  storageSet('localStorage', STATE_KEY, JSON.stringify({ state, at: Date.now() }));
}

/* ---- the page ---------------------------------------------------------------- */

/** Milliseconds since the load event ended, by the navigation entry. */
function sinceLoad() {
  try {
    const nav = performance.getEntriesByType('navigation')[0];
    if (nav && nav.loadEventEnd > 0) return performance.now() - nav.loadEventEnd;
  } catch {
    /* fall through to the conservative answer */
  }
  return 0;
}

function boot() {
  const dialog = document.getElementById('MabEmailPopup');
  if (!(dialog instanceof HTMLDialogElement) || typeof dialog.showModal !== 'function') return;
  if (isCheckoutPath(location.pathname)) return;
  // Shopify's captcha page: nothing is read, cleared or armed here, so the
  // marker comes through the round trip and nothing covers the captcha.
  if (isChallengePath(location.pathname)) return;

  const designMode = dialog.dataset.designMode === 'true';
  const productPage = dialog.dataset.productPage === 'true';
  const delayMs = delayFrom(dialog.dataset.delaySeconds);
  const marker = storageGet('sessionStorage', POSTED_KEY);

  const posted = !!dialog.querySelector('[data-mab-popup-success]') || /[?&]customer_posted=true(&|$)/.test(location.search);
  const hasError = !!dialog.querySelector('[data-mab-popup-error]');
  // Cleared only on the load that shows a submit's result.
  if (markerHandled({ posted, hasError })) storageRemove('sessionStorage', POSTED_KEY);
  const decision = decideOnLoad({
    checkoutPath: false,
    designMode,
    posted,
    hasError,
    marker,
    now: Date.now(),
    storageOk: designMode ? true : localStorageWorks(),
    stored: storageGet('localStorage', STATE_KEY),
    shown: storageGet('sessionStorage', SHOWN_KEY) === '1',
    knownSubscriber: dialog.dataset.knownSubscriber === 'true',
  });

  if (decision.write === 'subscribed') writeState('subscribed');

  /**
   * What the close should record: dismissed, unless already signed up, in the
   * editor, or showing a server error (the shopper tried to join; the session
   * flag alone keeps it away for the rest of the visit).
   */
  const recordOnClose = designMode || decision.action === 'success' || decision.action === 'error' ? null : 'dismissed';
  /** @type {Element | null} */
  let returnFocusTo = null;

  const open = () => {
    if (dialog.open) return;
    const active = document.activeElement;
    returnFocusTo = active instanceof HTMLElement && active !== document.body ? active : null;
    if (!designMode) storageSet('sessionStorage', SHOWN_KEY, '1');
    try {
      dialog.showModal();
    } catch {
      /* another modal won the race; the page is no worse off */
    }
  };

  dialog.addEventListener('close', () => {
    if (recordOnClose) writeState(recordOnClose);
    const target = returnFocusTo;
    returnFocusTo = null;
    if (target && target.isConnected && typeof target.focus === 'function') {
      target.focus({ preventScroll: true });
    }
  });

  /** Where the last press began, so a drag out of the panel is not a backdrop click. */
  let down = null;
  dialog.addEventListener('pointerdown', (event) => {
    down = { target: event.target, x: event.clientX, y: event.clientY };
  });

  dialog.addEventListener('click', (event) => {
    const press = down;
    down = null;
    const action = event.target instanceof Element ? event.target.closest('[data-mab-popup-action]') : null;
    if (action?.getAttribute('data-mab-popup-action') === 'close') {
      dialog.close();
      return;
    }
    if (action?.getAttribute('data-mab-popup-action') === 'copy') {
      copyCode(dialog, action);
      return;
    }
    // A click on the backdrop lands on the dialog itself, outside its box,
    // and so must the press that started it.
    if (isBackdropClick(press, { target: event.target, x: event.clientX, y: event.clientY }, dialog, dialog.getBoundingClientRect())) {
      dialog.close();
    }
  });

  dialog.querySelector('#MabEmailPopupForm')?.addEventListener('submit', () => {
    storageSet('sessionStorage', POSTED_KEY, String(Date.now()));
  });

  if (decision.action === 'design') {
    open();
    return;
  }
  // The success and error reopens answer the shopper's own submit, so they
  // wait for a clear moment on every page, product pages included.
  if (decision.action === 'success') {
    whenClear(dialog, open);
    return;
  }
  if (decision.action === 'error') {
    whenClear(dialog, () => {
      open();
      const field = dialog.querySelector('#MabEmailPopupEmail');
      if (field instanceof HTMLElement) field.focus();
    });
    return;
  }
  if (decision.action !== 'arm') return;

  arm(dialog, open, planTriggers({ productPage, exitArmed: exitIntentArmed(window.matchMedia?.bind(window), window.innerWidth) }), delayMs);
}

/** Runs fn now, or as soon as nothing is in the way. */
function whenClear(dialog, fn) {
  if (!blockedBy(document, dialog, window)) {
    fn();
    return;
  }
  let done = false;
  const check = () => {
    if (done || blockedBy(document, dialog, window)) return;
    done = true;
    window.clearInterval(timer);
    document.removeEventListener('visitorConsentCollected', check);
    fn();
  };
  const timer = window.setInterval(check, RETRY_MS);
  // Shopify fires this when the privacy banner is answered; look again at once.
  document.addEventListener('visitorConsentCollected', check);
}

/**
 * Arms what the plan allows. The first trigger to open it disarms both. On a
 * product page an exit that meets something in the way does nothing and
 * queues nothing; the listener stays for a later, separate exit.
 */
function arm(dialog, open, plan, delayMs) {
  let fired = false;
  let timer = 0;

  const disarm = () => {
    fired = true;
    window.clearTimeout(timer);
    document.removeEventListener('mouseout', onMouseOut);
  };

  const fire = () => {
    if (fired) return;
    // A tab opened earlier in this visit may have used it up since.
    if (storageGet('sessionStorage', SHOWN_KEY) === '1') {
      disarm();
      return;
    }
    if (!plan.queueWhenBlocked) {
      if (blockedBy(document, dialog, window)) return;
      disarm();
      open();
      return;
    }
    disarm();
    whenClear(dialog, open);
  };

  function onMouseOut(event) {
    if (isExitEvent(event)) fire();
  }

  if (plan.timer) timer = window.setTimeout(fire, Math.max(0, delayMs - sinceLoad()));
  if (plan.exitIntent) document.addEventListener('mouseout', onMouseOut);
}

/** Copy the code, or, with no clipboard, select it so the shopper can copy it. */
function copyCode(dialog, button) {
  const code = dialog.querySelector('[data-mab-popup-code]');
  const live = dialog.querySelector('[data-mab-popup-live]');
  if (!code) return;
  const text = (code.textContent || '').trim();
  const copied = button.getAttribute('data-copied-label') || '';

  const selectCode = () => {
    try {
      const range = document.createRange();
      range.selectNodeContents(code);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    } catch {
      /* nothing more to offer; the code is on screen */
    }
  };
  const announce = () => {
    if (live) live.textContent = copied;
    if (copied) button.textContent = copied;
  };

  const clipboard = navigator.clipboard;
  if (!clipboard || typeof clipboard.writeText !== 'function') {
    selectCode();
    return;
  }
  clipboard.writeText(text).then(announce, selectCode);
}

function start() {
  try {
    boot();
  } catch {
    /* the popup is an extra; it never breaks the page */
  }
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  const idle = () => {
    if ('requestIdleCallback' in window) window.requestIdleCallback(start, { timeout: 2000 });
    else window.setTimeout(start, 1);
  };
  if (document.readyState === 'complete') idle();
  else window.addEventListener('load', idle, { once: true });
}
